import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Image as ImageIcon, ExternalLink, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface LogoCandidate {
  url: string;
  source: string;
  score: number;
  reasons?: Array<{
    signal: string;
    delta: number;
    description: string;
  }>;
  alt?: string;
  isSvg?: boolean;
  isFavicon?: boolean;
}

interface LogoSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorWebsite: string;
  selectedLogo?: string;
  onSelectLogo: (logoUrl: string) => void;
}

export const LogoSelectionDialog: React.FC<LogoSelectionDialogProps> = ({
  open,
  onOpenChange,
  vendorWebsite,
  selectedLogo,
  onSelectLogo,
}) => {
  const [candidates, setCandidates] = useState<LogoCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<string>('');
  const [customUrl, setCustomUrl] = useState('');

  // Fetch logo candidates when dialog opens
  useEffect(() => {
    if (open && vendorWebsite) {
      fetchLogoCandidates();
    }
  }, [open, vendorWebsite]);

  const fetchLogoCandidates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_GLYPH_API_URL}/discover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: vendorWebsite,
        }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Handle both old and new response formats
        let candidatesArray = [];
        
        // New format: candidates are in data.evidence.static.candidates
        if (data.data.evidence?.static?.candidates) {
          candidatesArray = data.data.evidence.static.candidates;
        }
        // Old format: candidates are directly in data.data.candidates
        else if (data.data.candidates) {
          candidatesArray = data.data.candidates;
        }

        setCandidates(candidatesArray);
        
        // Pre-select the best logo if available (same for both formats)
        if (data.data?.best?.url) {
          setSelectedCandidate(data.data.best.url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch logo candidates:', error);
      toast.error('Failed to fetch logo candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // For now, we'll use a data URL
      // In production, you might want to upload to a storage service
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setSelectedCandidate(dataUrl);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCustomUrlSubmit = () => {
    if (!customUrl.trim()) return;

    // Basic URL validation
    try {
      new URL(customUrl);
      setSelectedCandidate(customUrl);
      toast.success('Custom logo URL added');
    } catch (error) {
      toast.error('Please enter a valid URL');
    }
  };

  const handleConfirm = () => {
    if (selectedCandidate) {
      onSelectLogo(selectedCandidate);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl! max-h-[80vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>Select Vendor Logo</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Vendor Info */}
          <div className="text-sm text-muted-foreground">
            Vendor Website: <span className="font-medium text-foreground">{vendorWebsite}</span>
          </div>

          {/* Logo Candidates */}
          <div>
            <Label className="text-base font-medium mb-3 block">Suggested Logos</Label>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Fetching logos...</span>
              </div>
            ) : candidates.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {candidates.map((candidate, index) => (
                  <div
                    key={index}
                    className={`relative group cursor-pointer rounded-lg border-2 p-2 transition-all ${
                      selectedCandidate === candidate.url
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedCandidate(candidate.url)}
                  >
                    <div className="aspect-square flex items-center justify-center bg-muted rounded">
                      <img
                        src={candidate.url}
                        alt={`Logo option ${index + 1}`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    {selectedCandidate === candidate.url && (
                      <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground text-center">
                      {candidate.source && <div>{candidate.source}</div>}
                      {candidate.score !== undefined && (
                        <div>Score: {Math.round(candidate.score * 100)}%</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No logo candidates found
              </div>
            )}
          </div>

          {/* Custom Upload */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Or Upload Your Own</Label>
            
            {/* File Upload */}
            <div>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="cursor-pointer"
              />
              {uploading && (
                <div className="flex items-center mt-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Uploading...
                </div>
              )}
            </div>

            {/* Custom URL */}
            <div className="flex gap-2">
              <Input
                placeholder="Or enter image URL..."
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCustomUrlSubmit}
                disabled={!customUrl.trim()}
              >
                Add
              </Button>
            </div>
          </div>

          {/* Selected Preview */}
          {selectedCandidate && (
            <div className="space-y-2 pb-6">
              <Label className="text-base font-medium">Selected Logo</Label>
              <div className="flex items-center gap-4 p-4 border rounded-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-muted rounded">
                  <img
                    src={selectedCandidate}
                    alt="Selected logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">Logo Preview</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {selectedCandidate.startsWith('data:') ? 'Uploaded image' : selectedCandidate}
                  </div>
                </div>
                {selectedCandidate.startsWith('http') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(selectedCandidate, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedCandidate}>
            Select Logo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
